import React, { useEffect, useState, useImperativeHandle, useRef } from "react";
import { Tree } from "antd";
import { TextField } from "@material-ui/core";
import Search from "@material-ui/icons/Search";
import CircularProgress from "@material-ui/core/CircularProgress";
import { useDebounceFn, useUpdateEffect } from "ahooks";
import { useTranslation } from "@/langs/useTranslation";
import { getCompanyAccounts, getCompanyTreeAPI } from "@/apis/apiCollection";
import { useBoundingRect } from "@/helpers/useBoundingRect";
import { mergeTreeData } from "@/helpers/utils";
import useStyles from "./styles";
import "@/css/companyTree.css";

const CompanyTree = React.forwardRef(
  (
    {
      excludeKeys,
      checkable = true,
      checkStrictly = true,
      checkSeperatly,
      checkedKeys: checkedKeysFromProp,
      selectedKeys: selectedKeysFromProp,
      loadUsers,
      searchPlaceholder,
      onSelect,
      multiple
    },
    ref
  ) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const allKeys = useRef([]);
    const flattenCompanyTreeRef = useRef([]);
    const halfCheckedKeys = useRef([]);
    const [loading, setLoading] = useState(false);
    const [treeData, setTreeData] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [autoExpandParent, setAutoExpandParent] = useState(true);
    const [checkedKeys, setCheckedKeys] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [loadedKeys, setLoadedKeys] = useState([]);
    const [filteredTreeData, setFilteredTreeData] = useState([]);
    const timer = useRef(null);

    const [bbox, treeOuterRef] = useBoundingRect();

    const expKeys = new Set();

    const filterTreeBySearch = (data = []) => {
      if (!searchValue) {
        if (treeData.length > 0) {
          expKeys.add(treeData[0].key);
        }
        return treeData;
      }
      return data
        .map(item => {
          const titleLowerCase = item.title.toLowerCase();
          const itemTitle = item.title;
          // const num = item.num;
          const index = titleLowerCase.indexOf(searchValue.toLowerCase());
          const beforeStr = itemTitle.substr(0, index);
          const afterStr = itemTitle.substr(index + searchValue.length);
          const valueStr = itemTitle.substr(index, searchValue.length);

          let title = "";
          let childrenResult = filterTreeBySearch(item.children);

          if (index > -1) {
            if (searchValue !== "" && item.parent_id) {
              expKeys.add(item.parent_id);
            }
            title = (
              <span className="tree-outer">
                {beforeStr}
                <span className="site-tree-search-value">{valueStr}</span>
                {afterStr}
              </span>
            );
            return {
              title,
              key: item.key,
              children: childrenResult,
              name: itemTitle,
              checkable: item.checkable,
              isLeaf: item.isLeaf
            };
          } else if (childrenResult.length > 0) {
            if (searchValue !== "" && item.parent_id) {
              expKeys.add(item.parent_id);
            }
            title = <span className="tree-outer">{item.title}</span>;
            return {
              title,
              key: item.key,
              children: childrenResult,
              name: itemTitle,
              checkable: item.checkable,
              isLeaf: item.isLeaf
            };
          } else {
            return null;
          }
        })
        .filter(item => item ?? false);
    };

    const getParentIds = (selfId, flattenData) => {
      const parentIds = [];
      function recursive(id) {
        for (const item of flattenData) {
          if (item.id === id) {
            if (item.parent_company_id) {
              parentIds.push(item.parent_company_id);
            }
            recursive(item.parent_company_id);
            break;
          }
        }
      }
      recursive(selfId);
      return parentIds;
    };

    const recursiveGetChildren = startNode => {
      const results = [];
      function recursive(node) {
        if (node.children) {
          node.children.forEach(item => {
            results.push(item.key);
            recursive(item);
          });
        }
      }

      recursive(startNode);

      return results;
    };

    const getTreeData = async () => {
      try {
        setLoading(true);
        const { companyTree, flattenCompanyTree } = await getCompanyTreeAPI(
          loadUsers
        );
        flattenCompanyTreeRef.current = flattenCompanyTree;
        setTreeData(companyTree);
        if (!loadUsers) {
          if (selectedKeysFromProp && selectedKeysFromProp.length > 0) {
            const parentKeys = getParentIds(
              selectedKeysFromProp[0],
              flattenCompanyTree
            );
            if (parentKeys.length > 0) {
              setExpandedKeys(parentKeys);
            } else {
              setExpandedKeys(companyTree.map(d => d.key));
            }
            setSelectedKeys(selectedKeysFromProp);
          } else {
            setExpandedKeys(companyTree.map(d => d.key));
          }
        }
        // setLoadedKeys(
        //   flattenCompanyTree
        //     .filter(item => item.children && item.children.length > 0)
        //     .map(item => item.id)
        // );
        const _allKeys = flattenCompanyTree.map(item => item.id);
        // if (excludeKeys) {
        //   // if (!checkStrictly) {
        //   //   // excludeKeys包含自己和它所有父级节点id
        //   //   excludeKeys = excludeKeys.concat(
        //   //     excludeKeys.reduce(
        //   //       (p, v) => p.concat(getParentIds(v, flattenCompanyTree)),
        //   //       []
        //   //     )
        //   //   );
        //   //   // 去重
        //   //   excludeKeys = Array.from(new Set(excludeKeys));
        //   // }
        //   // 从所有keys中过滤掉被排除的key
        //   const allKeysWithoutExcludes = _allKeys.filter(
        //     key => !excludeKeys.includes(key)
        //   );
        //   setCheckedKeys(allKeysWithoutExcludes);
        // } else {
        //   setCheckedKeys(_allKeys);
        // }
        if (checkedKeysFromProp) {
          setCheckedKeys(checkedKeysFromProp);
        }
        allKeys.current = _allKeys;
      } finally {
        setLoading(false);
      }
    };

    const loadUsersData = async ({ key, children }) => {
      // if (children && children.length) {
      //   return Promise.resolve();
      // }
      const { users } = await getCompanyAccounts({ company_id: key });
      const userData = users.map(item => ({
        key: item.id,
        title: item.name,
        name: item.name,
        checkable: true,
        isLeaf: true
      }));

      setLoadedKeys(prev => [...prev, key]);

      if (!!searchValue) {
        setFilteredTreeData(prevData => mergeTreeData(prevData, key, userData));
      } else {
        setTreeData(prevData => mergeTreeData(prevData, key, userData));
      }
    };

    const onExpand = expandedKeys => {
      setExpandedKeys(expandedKeys);
      setAutoExpandParent(false);
    };

    const onChange = e => {
      const { value } = e.target;
      setSearchValue(value);
    };

    const onCheck = (checkedKeys, event) => {
      if (checkStrictly && !checkSeperatly) {
        const nodeChildrenkeys = recursiveGetChildren(event.node);
        if (event.checked) {
          const parentKeys = getParentIds(
            event.node.key,
            flattenCompanyTreeRef.current
          );
          setCheckedKeys(
            Array.from(
              new Set([
                ...event.checkedNodes.map(node => node.key),
                ...nodeChildrenkeys,
                ...parentKeys
              ])
            )
          );
        } else {
          setCheckedKeys(prevChecks =>
            prevChecks.filter(
              key => key !== event.node.key && !nodeChildrenkeys.includes(key)
            )
          );
        }
      } else {
        setCheckedKeys(event.checkedNodes.map(node => node.key));
      }

      halfCheckedKeys.current = event.halfCheckedKeys || [];
    };

    const { run: handleSelect } = useDebounceFn(
      (selectedKeys, info) => {
        setSelectedKeys(selectedKeys);
        onSelect?.(selectedKeys, info);
      },
      { leading: true, wait: 300, trailing: false }
    );

    const clearTimer = () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };

    useImperativeHandle(ref, () => {
      return {
        getCheckedKeys: () => {
          return checkedKeys;
        },
        getAllKeys: () => {
          return allKeys.current;
        },
        getExcludeKeys: () => {
          return allKeys.current.filter(
            item =>
              !checkedKeys.includes(item) &&
              !halfCheckedKeys.current.includes(item)
          );
        }
      };
    });

    useEffect(() => {
      getTreeData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useUpdateEffect(() => {
      timer.current = setTimeout(() => {
        setFilteredTreeData(filterTreeBySearch(treeData));
        setExpandedKeys(Array.from(expKeys));
      }, 200);

      return clearTimer;
    }, [searchValue]);

    const finalTreeData = !!searchValue ? filteredTreeData : treeData;

    return (
      <>
        <div className={classes.searchInput}>
          <TextField
            label={searchPlaceholder || t({ defaultMessage: "搜索公司" })}
            variant="outlined"
            size="small"
            onChange={onChange}
          />
          <Search className={classes.searchIcon} color="action" />
        </div>
        {/* <Paper elevation={0} className={classes.companyTreeContent}> */}
        {loading ? (
          <CircularProgress size={30} className={classes.loading} />
        ) : finalTreeData.length > 0 ? (
          <div className={classes.treeOuter} id="treeOuter" ref={treeOuterRef}>
            <Tree
              checkable={checkable}
              checkStrictly={checkStrictly}
              defaultExpandAll={true}
              showIcon={false}
              onExpand={onExpand}
              defaultSelectedKeys={[]}
              treeData={finalTreeData}
              expandedKeys={expandedKeys}
              autoExpandParent={autoExpandParent}
              onSelect={handleSelect}
              selectedKeys={selectedKeys}
              selectable={!loadUsers}
              onCheck={onCheck}
              checkedKeys={checkedKeys}
              multiple={multiple}
              loadData={loadUsers ? loadUsersData : undefined}
              loadedKeys={loadedKeys}
              height={bbox.height ? bbox.height : 390}
            />
          </div>
        ) : (
          <div style={{ textAlign: "center" }}>
            <p>{t({ defaultMessage: "暂无数据" })}</p>
          </div>
        )}
        {/* </Paper> */}
      </>
    );
  }
);

export default CompanyTree;
